import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileText, FileSpreadsheet, Sunrise, Sunset, Users, Briefcase } from "lucide-react";
import api, { getToken } from "@/lib/api";
import StatusPill from "@/components/StatusPill";
import SummaryCard from "@/components/SummaryCard";
import UnmatchedPanel from "@/components/UnmatchedPanel";
import { toast } from "sonner";

/**
 * Past-Day (End-of-Day) view. Two tabs:
 *   1. All Staff — every expected staffer for the date with full attendance.
 *   2. AM / PM Care — admin-curated roster from Settings → AM/PM Care.
 *      Same status logic as tab 1, just filtered to the care roster. Used
 *      to verify sign-out times for extra-pay calculations.
 */
function nameLastFirst(full) {
  if (!full) return "";
  const s = String(full).trim();
  if (s.includes(",")) return s; // already "Last, First"
  const parts = s.split(/\s+/);
  if (parts.length === 1) return parts[0];
  const last = parts[parts.length - 1];
  const first = parts.slice(0, -1).join(" ");
  return `${last}, ${first}`;
}

function CareChip({ value }) {
  if (value === "am")
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold tracking-wide bg-amber-100 text-amber-900"><Sunrise className="w-3 h-3" /> AM</span>;
  if (value === "pm")
    return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold tracking-wide bg-indigo-100 text-indigo-900"><Sunset className="w-3 h-3" /> PM</span>;
  return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold tracking-wide bg-emerald-100 text-emerald-900"><Sunrise className="w-3 h-3" /><Sunset className="w-3 h-3" /> Both</span>;
}

export default function PastDay() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialDate = searchParams.get("date") || new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(initialDate);
  const [data, setData] = useState(null);
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("all"); // "all" | "care" | "vendors"
  const [careData, setCareData] = useState(null);
  const [careLoading, setCareLoading] = useState(false);
  const [vendorData, setVendorData] = useState(null);
  const [vendorLoading, setVendorLoading] = useState(false);

  useEffect(() => { if (!getToken()) navigate("/"); }, [navigate]);

  useEffect(() => {
    api.get("/archive/list", { params: { limit: 60 } }).then((r) => setArchives(r.data.items || [])).catch(() => {});
  }, []);

  const load = async (d) => {
    setLoading(true);
    try {
      const res = await api.get("/crew-dashboard/by-date", { params: { date: d } });
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load that date");
    } finally {
      setLoading(false);
    }
  };

  const loadCare = async (d) => {
    setCareLoading(true);
    try {
      const res = await api.get("/care-roster/by-date", { params: { date: d } });
      setCareData(res.data);
    } catch {
      toast.error("Failed to load AM/PM Care list");
    } finally {
      setCareLoading(false);
    }
  };

  const loadVendors = async (d) => {
    setVendorLoading(true);
    try {
      const res = await api.get("/vendors/by-date", { params: { date: d } });
      setVendorData(res.data);
    } catch {
      toast.error("Failed to load Vendors list");
    } finally {
      setVendorLoading(false);
    }
  };

  useEffect(() => { load(date); }, []);
  useEffect(() => {
    if (tab === "care") loadCare(date);
    if (tab === "vendors") loadVendors(date);
  }, [tab, date]);

  const onChangeDate = (e) => { const d = e.target.value; setDate(d); load(d); };

  const exportFile = async (kind) => {
    try {
      const res = await api.get(`/export/${kind}`, { params: { date }, responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `rrdc-attendance-${date}.${kind === "csv" ? "csv" : "xlsx"}`;
      a.click();
    } catch { toast.error("Export failed"); }
  };

  const summary = data?.summary || {};
  const records = data?.records || [];
  const careItems = careData?.items || [];
  const vendorItems = vendorData?.items || [];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200">
        <div className="max-w-[1500px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/dashboard")} className="btn-touch inline-flex items-center gap-1 px-3 rounded-lg text-slate-700 hover:bg-slate-100" data-testid="back-to-dashboard">
              <ArrowLeft className="w-4 h-4" /> Today
            </button>
            <img src="/logo.png" alt="Rolling River Day Camp" className="w-10 h-10 rounded-xl object-contain bg-white shadow-sm p-0.5" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Rolling River Day Camp</div>
              <h1 className="font-display text-lg font-bold text-slate-900">Past Day View</h1>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <input type="date" value={date} onChange={onChangeDate} className="px-3 py-2 rounded-lg border border-slate-200 text-sm" data-testid="past-date-picker" />
            {data?.is_final && (
              <span className="px-2.5 py-1 rounded-md bg-emerald-600 text-white font-bold text-[11px] tracking-[0.16em] uppercase" data-testid="final-locked-badge">
                FINAL — Locked
              </span>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-4 sm:px-6 py-6">
        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => exportFile("csv")} className="btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold" data-testid="past-export-csv">
            <FileText className="w-4 h-4" /> CSV
          </button>
          <button onClick={() => exportFile("xlsx")} className="btn-touch inline-flex items-center gap-2 px-4 rounded-xl bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold" data-testid="past-export-xlsx">
            <FileSpreadsheet className="w-4 h-4" /> Excel
          </button>
          {archives.length > 0 && (
            <select
              onChange={(e) => { setDate(e.target.value); load(e.target.value); }}
              className="px-3 py-2 rounded-lg border border-slate-200 text-sm"
              value={date}
              data-testid="archive-select"
            >
              {!archives.find((a) => a.date === date) && <option value={date}>{date}</option>}
              {archives.map((a) => (
                <option key={a.date} value={a.date}>{a.date} {a.summary?.absent ? `(${a.summary.absent} absent)` : ""}</option>
              ))}
            </select>
          )}
        </div>

        {/* Summary cards — only shown on All Staff tab */}
        {tab === "all" && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-6">
            <SummaryCard kind="total_expected" value={summary.total_expected} />
            <SummaryCard kind="present" value={summary.present} />
            <SummaryCard kind="late" value={summary.late} />
            <SummaryCard kind="late_arrival" value={summary.late_arrival} />
            <SummaryCard kind="absent" value={summary.absent} />
            <SummaryCard kind="signed_out" value={summary.signed_out} />
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            onClick={() => setTab("all")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm border ${tab === "all" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
            data-testid="past-tab-all"
          >
            <Users className="w-4 h-4" /> All Staff
          </button>
          <button
            onClick={() => setTab("care")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm border ${tab === "care" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
            data-testid="past-tab-care"
          >
            <Sunrise className="w-4 h-4 text-amber-500" /><Sunset className="w-4 h-4 text-indigo-500" /> AM / PM Care
            {careItems.length > 0 && <span className="ml-1 text-xs opacity-70">({careItems.length})</span>}
          </button>
          <button
            onClick={() => setTab("vendors")}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-semibold text-sm border ${tab === "vendors" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"}`}
            data-testid="past-tab-vendors"
          >
            <Briefcase className="w-4 h-4" /> Vendors
            {vendorItems.length > 0 && <span className="ml-1 text-xs opacity-70">({vendorItems.length})</span>}
          </button>
        </div>

        {tab === "all" && (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Name</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Status</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Sign-In</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Sign-Out</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Visit Type</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Device</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">Loading…</td></tr>
                )}
                {!loading && records.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500" data-testid="past-empty">No records for this date.</td></tr>
                )}
                {!loading && records.map((r) => (
                  <tr key={r.member_id || r.crew_id} className="border-t border-slate-100">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-slate-900">{r.full_name}</div>
                      <div className="text-xs text-slate-500">{r.email}</div>
                    </td>
                    <td className="px-4 py-3"><StatusPill status={r.status} prior={r.prior_status} /></td>
                    <td className="px-4 py-3 tabular-nums text-slate-700">{r.check_in_local_display || r.check_in_time || "—"}</td>
                    <td className="px-4 py-3 tabular-nums text-slate-700">{r.sign_out_local_display || r.sign_out_time || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.visit_type || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{r.device_name || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "care" && (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Name (Last, First)</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Care</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Status</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Sign-In</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Sign-Out</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Division</th>
                </tr>
              </thead>
              <tbody>
                {careLoading && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">Loading…</td></tr>
                )}
                {!careLoading && careItems.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500" data-testid="care-tab-empty">
                    No staff on the AM/PM Care roster.
                    <div className="mt-1 text-xs">Add staff in <strong>Settings → Performance → AM / PM Care Roster</strong>.</div>
                  </td></tr>
                )}
                {!careLoading && careItems.map((r) => {
                  const lateOrOut = r.status === "Late" || r.status === "Late Arrival" || r.status === "Signed Out";
                  return (
                    <tr key={r.crew_id} className="border-t border-slate-100" data-testid={`care-row-${r.crew_id}`}>
                      <td className="px-4 py-3 font-semibold text-slate-900">{nameLastFirst(r.name)}</td>
                      <td className="px-4 py-3"><CareChip value={r.care_type} /></td>
                      <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                      <td className={`px-4 py-3 tabular-nums ${lateOrOut ? "font-semibold text-slate-900" : "text-slate-700"}`}>{r.sign_in || "—"}</td>
                      <td className={`px-4 py-3 tabular-nums ${lateOrOut ? "font-semibold text-slate-900" : "text-slate-700"}`}>{r.sign_out || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{r.division || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {tab === "vendors" && (
          <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Name (Last, First)</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Status</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Sign-In</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Sign-Out</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Position</th>
                  <th className="text-left px-4 py-3 text-[11px] uppercase tracking-[0.16em] text-slate-500">Division</th>
                </tr>
              </thead>
              <tbody>
                {vendorLoading && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">Loading…</td></tr>
                )}
                {!vendorLoading && vendorItems.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500" data-testid="vendor-tab-empty">
                    No vendors on the roster yet.
                    <div className="mt-1 text-xs">Add vendors in <strong>Settings → Performance → Outside Vendors</strong>.</div>
                  </td></tr>
                )}
                {!vendorLoading && vendorItems.map((r) => {
                  const lateOrOut = r.status === "Late" || r.status === "Late Arrival" || r.status === "Signed Out";
                  return (
                    <tr key={r.crew_id} className="border-t border-slate-100" data-testid={`vendor-tab-row-${r.crew_id}`}>
                      <td className="px-4 py-3 font-semibold text-slate-900">{nameLastFirst(r.name)}</td>
                      <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                      <td className={`px-4 py-3 tabular-nums ${lateOrOut ? "font-semibold text-slate-900" : "text-slate-700"}`}>{r.sign_in || "—"}</td>
                      <td className={`px-4 py-3 tabular-nums ${lateOrOut ? "font-semibold text-slate-900" : "text-slate-700"}`}>{r.sign_out || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{r.position || "—"}</td>
                      <td className="px-4 py-3 text-slate-600">{r.division || "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {tab === "all" && <UnmatchedPanel items={data?.unmatched || []} />}
      </main>
    </div>
  );
}
